import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useUpdateEmployeeMutation } from "modules/shared/hooks/useUpdateEmployeeMutation"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpdateEmployeeSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"

type ChangePasswordModalProps = {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
}

export const ChangePasswordModal = ({ isOpen, setIsOpen }: ChangePasswordModalProps) => {
  const { t } = useTranslation()
  const form = useForm<z.infer<typeof UpdateEmployeeSchema>>({
    resolver: zodResolver(UpdateEmployeeSchema),
    defaultValues: { password: "", currentPassword: "", confirmNewPassword: "" },
  })
  const { mutateAsync, isPending } = useUpdateEmployeeMutation()

  const onSubmit = async (data: z.infer<typeof UpdateEmployeeSchema>) => {
    await mutateAsync(data)
    form.reset()
    form.trigger()
    toast.success(t("shared.changePasswordModal.success"))
    setIsOpen(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => setIsOpen(false)}>
      <DialogContent>
        <Form
          {...form}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
          errorTranslations={getErrorTranslations()}
        >
          <DialogHeader>
            <DialogTitle>{t("shared.changePasswordModal.title")}</DialogTitle>
            <DialogDescription>{t("shared.changePasswordModal.description")}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name={"currentPassword"}
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("shared.changePasswordModal.form.currentPassword")}</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" required />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={"password"}
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("shared.changePasswordModal.form.password")}</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" required />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={"confirmNewPassword"}
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("shared.changePasswordModal.form.confirmNewPassword")}</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" required />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter className="mt-6">
            <div className="flex justify-between w-full">
              <Button variant="outline" type="button">
                {t("shared.changePasswordModal.form.cancel")}
              </Button>
              <Button type="submit" disabled={!form.formState.isValid || isPending}>
                {t("shared.changePasswordModal.form.saveChanges")}
              </Button>
            </div>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
